// City "vibe" motifs, ported 1:1 from the v10 prototype's `drawDecor()`.
// Each motif is a small, self-contained draw function keyed by the id a
// `LevelDef.decor` array names; add a new city motif by adding one entry to
// `MOTIFS` below — nothing else needs to know about it.

export type MotifFn = (ctx: CanvasRenderingContext2D, W: number, H: number) => void;

function frac(x: number): number { return x - Math.floor(x); }
/** deterministic pseudo-random in [0,1), purely a function of (i, salt) — no Math.random */
function rnd(i: number, salt: number): number { return frac(Math.sin(i * 12.9898 + salt * 78.233) * 43758.5453); }

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r); ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r); ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function roofCluster(ctx: CanvasRenderingContext2D, x: number, y: number): void {
  for (let i = 0; i < 3; i++) {
    const ox = x + (i - 1) * 13 + rnd(i, 5) * 4, oy = y + (i % 2) * 9;
    ctx.fillStyle = 'rgba(235,228,216,0.9)';
    ctx.fillRect(ox - 5, oy - 2, 10, 7);
    ctx.fillStyle = 'rgba(214,138,113,0.85)';
    ctx.beginPath(); ctx.moveTo(ox - 6, oy - 2); ctx.lineTo(ox, oy - 8); ctx.lineTo(ox + 6, oy - 2); ctx.closePath(); ctx.fill();
  }
}

function drawSpire(ctx: CanvasRenderingContext2D, x: number, y: number): void {
  ctx.fillStyle = 'rgba(110,98,104,0.55)';
  ctx.fillRect(x - 2.5, y - 14, 5, 14);
  ctx.beginPath(); ctx.moveTo(x - 3.5, y - 14); ctx.lineTo(x, y - 24); ctx.lineTo(x + 3.5, y - 14); ctx.closePath(); ctx.fill();
  ctx.fillStyle = 'rgba(214,138,113,0.85)';
  ctx.beginPath(); ctx.arc(x, y - 24, 1.4, 0, Math.PI * 2); ctx.fill();
}

function drawToriiGate(ctx: CanvasRenderingContext2D, x: number, y: number): void {
  ctx.fillStyle = 'rgba(208,72,62,0.8)';
  ctx.fillRect(x - 8, y - 2, 3, 14); ctx.fillRect(x + 5, y - 2, 3, 14);   // posts
  ctx.fillRect(x - 9, y - 1, 18, 2.6);                                    // lower beam
  ctx.beginPath();                                                        // curved top beam
  ctx.moveTo(x - 12, y - 6); ctx.quadraticCurveTo(x, y - 9.5, x + 12, y - 6);
  ctx.quadraticCurveTo(x, y - 6.5, x - 12, y - 6); ctx.closePath(); ctx.fill();
}

function drawBoat(ctx: CanvasRenderingContext2D, x: number, y: number): void {
  ctx.strokeStyle = 'rgba(255,255,255,0.5)'; ctx.lineWidth = 1.4;
  ctx.beginPath(); ctx.arc(x - 9, y + 5, 7, 0.15 * Math.PI, 0.85 * Math.PI); ctx.stroke();
  ctx.fillStyle = 'rgba(255,255,255,0.95)';
  ctx.beginPath(); ctx.moveTo(x, y + 2); ctx.lineTo(x, y - 8); ctx.lineTo(x + 6, y + 2); ctx.closePath(); ctx.fill();
  ctx.strokeStyle = 'rgba(90,100,110,0.7)'; ctx.lineWidth = 1.6;
  ctx.beginPath(); ctx.moveTo(x - 4, y + 3.5); ctx.lineTo(x + 7, y + 3.5); ctx.stroke();
}

function drawLighthouse(ctx: CanvasRenderingContext2D, x: number, y: number): void {
  ctx.fillStyle = 'rgba(255,255,255,0.95)';
  ctx.fillRect(x - 2.5, y - 4, 5, 11);
  ctx.fillStyle = 'rgba(208,72,62,0.9)';
  ctx.fillRect(x - 2.5, y - 8, 5, 4);
  ctx.fillStyle = 'rgba(59,59,59,0.5)';
  ctx.fillRect(x - 3.5, y + 7, 7, 1.6);
}

// ---------- shapes for the later cities ----------

/** A tapering spire with a little finial — Sagrada Família, stylised. */
function drawSagradaSpire(ctx: CanvasRenderingContext2D, x: number, y: number, h: number): void {
  ctx.fillStyle = 'rgba(196,166,128,0.55)';
  ctx.beginPath();
  ctx.moveTo(x - 3.2, y); ctx.lineTo(x - 1.1, y - h); ctx.lineTo(x + 1.1, y - h); ctx.lineTo(x + 3.2, y);
  ctx.closePath(); ctx.fill();
  ctx.fillStyle = 'rgba(214,138,113,0.8)';
  ctx.beginPath(); ctx.arc(x, y - h - 1.6, 1.5, 0, Math.PI * 2); ctx.fill();
}

/** A scatter of broken glazed tiles — trencadís, the Park Güell mosaic. */
function drawTiles(ctx: CanvasRenderingContext2D, x: number, y: number, salt: number): void {
  const hues = ['rgba(214,138,113,0.55)', 'rgba(122,168,176,0.5)', 'rgba(226,196,120,0.55)', 'rgba(150,160,140,0.45)'];
  for (let i = 0; i < 7; i++) {
    const ox = x + (rnd(i, salt) - 0.5) * 26, oy = y + (rnd(i, salt + 1) - 0.5) * 22;
    const s = 3 + rnd(i, salt + 2) * 3;
    ctx.save();
    ctx.translate(ox, oy); ctx.rotate(rnd(i, salt + 3) * Math.PI);
    ctx.fillStyle = hues[i % hues.length];
    ctx.fillRect(-s / 2, -s / 2, s, s);
    ctx.restore();
  }
}

/** Three overlapping shells — the Opera House from across the water. */
function drawSails(ctx: CanvasRenderingContext2D, x: number, y: number): void {
  ctx.fillStyle = 'rgba(255,255,255,0.9)';
  for (const [ox, h, w] of [[-9, 9, 7], [0, 14, 8], [9, 11, 7]] as [number, number, number][]) {
    ctx.beginPath();
    ctx.moveTo(x + ox - w / 2, y);
    ctx.quadraticCurveTo(x + ox - w / 2, y - h, x + ox + w / 2, y - h * 0.35);
    ctx.closePath(); ctx.fill();
  }
  ctx.fillStyle = 'rgba(59,59,59,0.25)';
  ctx.fillRect(x - 15, y, 30, 1.6);
}

/** A single steel arch with its deck — the Harbour Bridge, stylised. */
function drawArchBridge(ctx: CanvasRenderingContext2D, x: number, y: number, span: number): void {
  ctx.strokeStyle = 'rgba(90,100,110,0.55)';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(x - span / 2, y);
  ctx.quadraticCurveTo(x, y - span * 0.42, x + span / 2, y);
  ctx.stroke();
  ctx.strokeStyle = 'rgba(90,100,110,0.4)';
  ctx.lineWidth = 1.4;
  ctx.beginPath(); ctx.moveTo(x - span / 2 - 4, y + 3); ctx.lineTo(x + span / 2 + 4, y + 3); ctx.stroke();
}

/** A lead dome on a drum, with two smaller ones at its shoulders. */
function drawDome(ctx: CanvasRenderingContext2D, x: number, y: number, r: number): void {
  ctx.fillStyle = 'rgba(140,150,152,0.5)';
  for (const [ox, rr] of [[-r * 1.25, r * 0.55], [r * 1.25, r * 0.55], [0, r]] as [number, number][]) {
    ctx.beginPath(); ctx.arc(x + ox, y, rr, Math.PI, 0); ctx.fill();
    ctx.fillRect(x + ox - rr, y, rr * 2, 1.8);
  }
  ctx.strokeStyle = 'rgba(140,150,152,0.6)'; ctx.lineWidth = 1.2;
  ctx.beginPath(); ctx.moveTo(x, y - r); ctx.lineTo(x, y - r - 4); ctx.stroke();
}

/** A pencil minaret with a conical cap. */
function drawMinaret(ctx: CanvasRenderingContext2D, x: number, y: number, h: number): void {
  ctx.fillStyle = 'rgba(140,150,152,0.5)';
  ctx.fillRect(x - 1.5, y - h, 3, h);
  ctx.beginPath();
  ctx.moveTo(x - 1.9, y - h); ctx.lineTo(x, y - h - 7); ctx.lineTo(x + 1.9, y - h); ctx.closePath(); ctx.fill();
  ctx.fillRect(x - 2.8, y - h * 0.55, 5.6, 1.2);   // balcony
}

/** Two towers and the main cable slung between them. */
function drawSuspensionTowers(ctx: CanvasRenderingContext2D, x: number, y: number, span: number): void {
  const h = 20;
  ctx.fillStyle = 'rgba(196,86,64,0.6)';
  for (const ox of [-span / 2, span / 2]) {
    ctx.fillRect(x + ox - 2, y - h, 4, h);
    ctx.fillRect(x + ox - 5, y - h * 0.72, 10, 1.8);
    ctx.fillRect(x + ox - 5, y - h * 0.34, 10, 1.8);
  }
  ctx.strokeStyle = 'rgba(196,86,64,0.45)'; ctx.lineWidth = 1.4;
  ctx.beginPath();
  ctx.moveTo(x - span / 2, y - h);
  ctx.quadraticCurveTo(x, y - h * 0.25, x + span / 2, y - h);
  ctx.stroke();
}

/** A tower mill with its four sails. */
function drawWindmill(ctx: CanvasRenderingContext2D, x: number, y: number): void {
  ctx.fillStyle = 'rgba(110,98,104,0.5)';
  ctx.beginPath();
  ctx.moveTo(x - 5, y); ctx.lineTo(x - 3, y - 13); ctx.lineTo(x + 3, y - 13); ctx.lineTo(x + 5, y);
  ctx.closePath(); ctx.fill();
  ctx.strokeStyle = 'rgba(110,98,104,0.55)'; ctx.lineWidth = 1.6; ctx.lineCap = 'round';
  const a = Math.PI / 7;  // parked at a gentle angle — nothing in the decor animates
  for (let i = 0; i < 4; i++) {
    const th = a + i * Math.PI / 2;
    ctx.beginPath();
    ctx.moveTo(x, y - 15);
    ctx.lineTo(x + Math.cos(th) * 11, y - 15 + Math.sin(th) * 11);
    ctx.stroke();
  }
}

// ---------- motif registry: id → draw at this level's fixed screen fractions ----------
// Positions are the prototype's per-city fractions (`drawDecor()`), one entry per motif id.

export const MOTIFS: Record<string, MotifFn> = {
  roofs(ctx, W, H) {
    roofCluster(ctx, W * 0.11, H * 0.13);
    roofCluster(ctx, W * 0.84, H * 0.18);
    roofCluster(ctx, W * 0.13, H * 0.87);
  },
  spire(ctx, W, H) {
    drawSpire(ctx, W * 0.88, H * 0.85);
  },
  sakura(ctx, W, H) {
    for (let i = 0; i < 14; i++) {
      const x = W * rnd(i, 1), y = H * (0.06 + 0.9 * rnd(i, 2));
      ctx.fillStyle = `rgba(238,183,199,${0.35 + 0.3 * rnd(i, 3)})`;
      ctx.beginPath(); ctx.arc(x, y, 1.8 + 1.6 * rnd(i, 4), 0, Math.PI * 2); ctx.fill();
    }
  },
  torii(ctx, W, H) {
    drawToriiGate(ctx, W * 0.14, H * 0.15);
    drawToriiGate(ctx, W * 0.86, H * 0.86);
  },
  boats(ctx, W, H) {
    drawBoat(ctx, W * 0.87, H * 0.28);
    drawBoat(ctx, W * 0.80, H * 0.52);
    drawBoat(ctx, W * 0.90, H * 0.70);
  },
  lighthouse(ctx, W, H) {
    drawLighthouse(ctx, W * 0.76, H * 0.10);
  },
  sagrada(ctx, W, H) {
    drawSagradaSpire(ctx, W * 0.13, H * 0.13, 26);
    drawSagradaSpire(ctx, W * 0.19, H * 0.14, 19);
    drawSagradaSpire(ctx, W * 0.86, H * 0.79, 22);
  },
  mosaic(ctx, W, H) {
    drawTiles(ctx, W * 0.85, H * 0.14, 11);
    drawTiles(ctx, W * 0.12, H * 0.82, 17);
  },
  sails(ctx, W, H) {
    drawSails(ctx, W * 0.72, H * 0.125);
  },
  harbour(ctx, W, H) {
    drawArchBridge(ctx, W * 0.40, H * 0.10, 48);
  },
  domes(ctx, W, H) {
    drawDome(ctx, W * 0.16, H * 0.83, 7.5);
    drawDome(ctx, W * 0.87, H * 0.30, 5.5);
  },
  minarets(ctx, W, H) {
    drawMinaret(ctx, W * 0.09, H * 0.83, 17);
    drawMinaret(ctx, W * 0.24, H * 0.83, 17);
    drawMinaret(ctx, W * 0.92, H * 0.30, 13);
  },
  goldengate(ctx, W, H) {
    drawSuspensionTowers(ctx, W * 0.86, H * 0.12, 34);
  },
  fog(ctx, W, H) {
    // three soft bands rolling in off the bay — flat white, no motion
    for (let i = 0; i < 3; i++) {
      const y = H * (0.20 + 0.28 * i), h = 16 + i * 5;
      ctx.fillStyle = `rgba(255,255,255,${0.20 - i * 0.045})`;
      roundRect(ctx, W * (0.46 + 0.05 * rnd(i, 9)), y, W * 0.62, h, h / 2);
      ctx.fill();
    }
  },
  windmill(ctx, W, H) {
    drawWindmill(ctx, W * 0.13, H * 0.14);
    drawWindmill(ctx, W * 0.88, H * 0.88);
  },
  canals(ctx, W, H) {
    // the canal ring, three concentric arcs opening away from the field
    ctx.strokeStyle = 'rgba(150,178,182,0.42)';
    ctx.lineWidth = 2.2;
    for (let i = 0; i < 3; i++) {
      ctx.beginPath();
      ctx.arc(W * 0.5, H * 0.05, W * (0.30 + 0.11 * i), 0.16 * Math.PI, 0.84 * Math.PI);
      ctx.stroke();
    }
  },
};

export function drawMotif(ctx: CanvasRenderingContext2D, id: string, W: number, H: number): void {
  MOTIFS[id]?.(ctx, W, H);
}

export { roundRect };
